import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./cidp/styles.css";
import { api, decodeU8, decodeU16, decodeI32, priorityScores } from "./cidp/lib";
import MapView from "./cidp/MapView";
import ConfigPanel from "./cidp/ConfigPanel";
import neverflatLogo from "./cidp/assets/neverflat.png";
import ingartekLogo from "./cidp/assets/ingartek.png";

const LAYERS = [
  ["Input Data", "Population, land use, existing chargers and demand indicators on the coarse grid."],
  ["Coarse Distribution", "Forecast demand allocated across the grid by land use, centrality and population."],
  ["Electrical Grid Validation", "Added load validated against the grid model per node."],
  ["Prioritization Map", "AHP + MCDM priority score on the whole-city 200 m grid."],
  ["Fine Distribution", "EDA-distributed fast/slow chargers over each coarse square's 15×15 sub-grid."],
];

export default function App() {
  const [meta, setMeta] = useState(null);
  const [B, setB] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [layer, setLayer] = useState("Input Data");
  const [coarse, setCoarse] = useState(null);
  const [grid, setGrid] = useState(null);
  const [fineCounts, setFineCounts] = useState(null);
  const [committedMeso, setCommittedMeso] = useState(null);
  const [committedFine, setCommittedFine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calibCities, setCalibCities] = useState([]);

  const [city, setCity] = useState("Berlin, Germany");

  useEffect(() => {
    api.calibCities().then((d) => setCalibCities(d.cities || []));
    api.meta().then((m) => {
      setMeta({ ...m, center: m.cities[0].center, zoom: m.cities[0].zoom });
      const a = m.ahp;
      setCfg({
        ...m.demand_defaults,
        lu_weights_fast: { ...m.weight_defaults.lu_weights_fast },
        lu_weights_slow: { ...m.weight_defaults.lu_weights_slow },
        decay_fast: m.weight_defaults.decay_fast,
        decay_slow: m.weight_defaults.decay_slow,
        max_iterations: 3,
        meso_fast: { ...a.fast },
        meso_slow: { ...a.slow },
        meso_sel: "Fast",
        fine_viz: "Fast chargers",
        viz_dataset: "Population density",
        viz_lu: "Dominant",
        viz_exist_type: "Both",
        viz_exist_grid: "Coarse",
        viz_ind: a.labels[a.leaves[0]],
      });
    });
  }, []);

  useEffect(() => {
    if (!meta) return;
    const c = meta.cities.find((x) => x.name === city);
    if (!c || !c.available) { setB(null); return; }
    api.bundle(city).then((d) => {
      const nl = d.leaves.length;
      const vals = decodeU8(d.vals_b64);
      const mask = decodeU8(d.mask_b64);
      const pop = decodeU16(d.pop_b64);
      const ef = decodeU16(d.ef_b64);
      const es = decodeU16(d.es_b64);
      const tot = new Uint16Array(ef.length);
      for (let i = 0; i < ef.length; i++) tot[i] = ef[i] + es[i];
      setB({ ...d, vals, mask, pop, ef, es, tot, nl });
    });
  }, [city, meta]);

  const params = useMemo(() => {
    if (!cfg) return null;
    return {
      horizon_year: cfg.horizon_year,
      target_ev_share: cfg.target_ev_share,
      chargers_per_ev: cfg.chargers_per_ev,
      fast_share: cfg.fast_share,
      fast_power_kw: cfg.fast_power_kw,
      slow_power_kw: cfg.slow_power_kw,
      lu_weights_fast: cfg.lu_weights_fast,
      lu_weights_slow: cfg.lu_weights_slow,
      decay_fast: cfg.decay_fast,
      decay_slow: cfg.decay_slow,
    };
  }, [cfg]);

  const paramsHash = params ? JSON.stringify(params) : "";

  // coarse geojson follows params (needed by Input Data + Coarse)
  useEffect(() => {
    if (!params || !meta) return;
    const c = meta.cities.find((x) => x.name === city);
    if (!c || !c.available) { setCoarse(null); setGrid(null); setFineCounts(null); return; }
    api.coarse(city, params).then((d) => setCoarse(d.geojson));
  }, [paramsHash, city, meta]);

  const bounds = useMemo(() => {
    if (!coarse) return null;
    let latmin = 90, latmax = -90, lonmin = 180, lonmax = -180;
    coarse.features.forEach((f) =>
      f.geometry.coordinates[0].forEach(([lon, lat]) => {
        if (lat < latmin) latmin = lat;
        if (lat > latmax) latmax = lat;
        if (lon < lonmin) lonmin = lon;
        if (lon > lonmax) lonmax = lon;
      })
    );
    return [[latmin, lonmin], [latmax, lonmax]];
  }, [coarse]);

  const priorityScore = useMemo(() => {
    if (!B || !committedMeso) return null;
    const w = cfg.meso_sel === "Slow" ? committedMeso.slow : committedMeso.fast;
    return priorityScores(B.vals, B.mask, B.leaves, w);
  }, [B, committedMeso, cfg && cfg.meso_sel]);

  const actions = {
    validate: () => {
      setBusy(true);
      api.grid(city, params, true, cfg.max_iterations).then((d) => { setGrid(d); setBusy(false); });
    },
    calcMeso: () => setCommittedMeso({ fast: { ...cfg.meso_fast }, slow: { ...cfg.meso_slow } }),
    calcFine: () => {
      let w = committedMeso;
      if (!w) {
        w = { fast: { ...cfg.meso_fast }, slow: { ...cfg.meso_slow } };
        setCommittedMeso(w);
      }
      setCommittedFine(w);
      setBusy(true);
      api.fineCounts(city, params, w).then((d) => {
        setFineCounts({ fast: decodeI32(d.fast_b64), slow: decodeI32(d.slow_b64) });
        setBusy(false);
      });
    },
    calibrate: (calCity) => {
      setBusy(true);
      api.calibrate(calCity).then((d) => {
        set({
          lu_weights_fast: d.lu_weights_fast,
          decay_fast: d.decay_fast,
          lu_weights_slow: d.lu_weights_slow,
          decay_slow: d.decay_slow,
        });
        setBusy(false);
      }).catch(() => setBusy(false));
    },
  };

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const download = () => {
    const body = { layer: layerKey(layer), city, params };
    if (layer === "Prioritization Map") body.meso_weights = committedMeso;
    if (layer === "Fine Distribution") body.fine_weights = committedFine;
    api.download(body).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${layerKey(layer)}.geojson`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  if (!meta || !cfg) return <div className="cidp-loading">Loading…</div>;

  const cityObj = meta.cities.find((c) => c.name === city) || meta.cities[0];
  const available = cityObj.available;
  const viewCfg = { ...cfg, priorityScore, params_hash: paramsHash };
  const downloadable = ["Coarse Distribution", "Electrical Grid Validation", "Prioritization Map", "Fine Distribution"].includes(layer);
  const dlReady =
    available && (
    layer === "Coarse Distribution" ||
    (layer === "Electrical Grid Validation" && grid) ||
    (layer === "Prioritization Map" && committedMeso) ||
    (layer === "Fine Distribution" && committedFine));

  const gridViewReady = layer !== "Electrical Grid Validation" || grid;
  const prioReady = layer !== "Prioritization Map" || committedMeso;
  const fineReady = layer !== "Fine Distribution" || fineCounts;

  return (
    <div className="cidp-app">
      <header className="cidp-banner">
        <div className="cidp-title">Charging Infrastructure Deployment Planner</div>
        <div className="cidp-logos">
          <img src={neverflatLogo} alt="Neverflat" className="cidp-logo-never" data-testid="logo-neverflat" />
          <span className="cidp-sep" />
          <span className="cidp-logo-ingartek-wrap">
            <img src={ingartekLogo} alt="Ingartek" className="cidp-logo-ingartek" data-testid="logo-ingartek" />
          </span>
        </div>
      </header>

      <nav className="cidp-tabs" data-testid="layer-tabs">
        {LAYERS.map(([name], i) => (
          <button key={name} className={layer === name ? "on" : ""} onClick={() => setLayer(name)}
                  data-testid={`tab-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {i + 1} {name}
          </button>
        ))}
      </nav>

      <div className="cidp-desc">{LAYERS.find((l) => l[0] === layer)[1]}</div>

      <div className="cidp-body">
        <div className="cidp-map" data-testid="map-container">
          {!available && (
            <div className="cidp-status info" data-testid="city-unavailable">
              Only <b>Berlin</b> has layer data so far. No data yet for {city.split(",")[0]}.
            </div>
          )}
          {(available && layer === "Electrical Grid Validation" && grid && (grid.status === "skipped" || grid.status === "disabled")) && (
            <div className="cidp-status info" data-testid="grid-status">ℹ️ {grid.reason} The added-load distribution is shown without feasibility or excess-load results.</div>
          )}
          {(available && layer === "Electrical Grid Validation" && grid && grid.status === "infeasible") && (
            <div className="cidp-status err">⚠️ WARNING: the distribution could not be made electrically feasible. The electrical grid may need to be upgraded.</div>
          )}
          {(available && layer === "Electrical Grid Validation" && grid && grid.status === "ok") && (
            <div className="cidp-status ok">✅ Distribution is electrically feasible.</div>
          )}
          {(available && layer === "Electrical Grid Validation" && !grid) && (
            <div className="cidp-status info">Press ▶ Validate in the panel to check the added load against the grid model.</div>
          )}
          {(available && layer === "Prioritization Map" && !committedMeso) && (
            <div className="cidp-status info">Press ▶ Calculate priority map to compute scores.</div>
          )}
          {(available && layer === "Fine Distribution" && !fineCounts) && (
            <div className="cidp-status info">Press ▶ Calculate fine distribution to assign chargers.</div>
          )}
          <MapView
            layer={available && gridViewReady && prioReady && fineReady ? layer : "__none__"}
            meta={meta}
            B={available ? B : null}
            cfg={viewCfg}
            coarse={available ? coarse : null}
            grid={grid}
            fineCounts={layer === "Fine Distribution" ? fineCounts : null}
            bounds={available ? bounds : null}
            center={cityObj.center}
            zoom={cityObj.zoom}
            carto={meta.carto_key}
          />
        </div>

        <aside className="cidp-panel">
          <select className="cidp-city-select" value={city} onChange={(e) => setCity(e.target.value)} data-testid="city-picker">
            {meta.cities.map((c) => (
              <option key={c.name} value={c.name}>{c.name.split(",")[0]}</option>
            ))}
          </select>
          {downloadable && (
            <button className="cidp-btn dl" disabled={!dlReady} onClick={download} data-testid="btn-download">
              ⬇ Download GeoJSON
            </button>
          )}
          <ConfigPanel layer={layer} meta={meta} cfg={cfg} set={set} actions={actions} calibCities={calibCities} />
          {busy && <div className="cidp-cap">Computing…</div>}
        </aside>
      </div>
    </div>
  );
}

function layerKey(name) {
  return {
    "Coarse Distribution": "coarse",
    "Electrical Grid Validation": "grid",
    "Prioritization Map": "prioritization",
    "Fine Distribution": "fine",
  }[name];
}
